import { useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { CaptureFlow, type CaptureFlowHandle } from "../components/CaptureFlow";
import { FloatingAddButton } from "../components/FloatingAddButton";
import { IssueGrid } from "../components/IssueGrid";
import { formatIssueCount } from "../services/app/reportStatus";
import { conditionNotePath } from "../services/app/routes";
import { trackConditionOpened } from "../services/analytics/analyticsEvents";
import type { Issue, Report, Room } from "../services/db/schema";

interface RoomIssuesRouteProps {
  report: Report;
  room: Room;
  issues: Issue[];
  onNavigate: (path: string) => void;
  onChanged: () => void;
}

export function RoomIssuesRoute({
  report,
  room,
  issues,
  onNavigate,
  onChanged
}: RoomIssuesRouteProps): JSX.Element {
  const captureRef = useRef<CaptureFlowHandle>(null);
  const [error, setError] = useState<string>();

  return (
    <section className="screen">
      <div className="topbar">
        <button className="icon-button" type="button" aria-label="Back to rooms" onClick={() => onNavigate("/rooms")}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div className="topbar__title">
          <h1>{room.name}</h1>
          <p>
            {formatIssueCount(issues.length)}
          </p>
        </div>
      </div>
      {error && <div className="notice notice--error">{error}</div>}
      <IssueGrid
        issues={issues}
        onOpenIssue={(issueId) => {
          trackConditionOpened();
          onNavigate(conditionNotePath(room.id, issueId));
        }}
      />
      <CaptureFlow
        ref={captureRef}
        reportId={report.id}
        roomId={room.id}
        onCreated={(issueId) => {
          onChanged();
          onNavigate(conditionNotePath(room.id, issueId));
        }}
        onError={setError}
      />
      <FloatingAddButton label="Take photo" onClick={() => captureRef.current?.open()} />
    </section>
  );
}
